// ------------------------------------------------------------------------------
// 初期設定 - Axios インターセプター
// ------------------------------------------------------------------------------
import {authOperations} from '../reducks/auth';
import {commonOperations} from '../reducks/common';
import {intl} from './globalIntl';
import store from './status';
import errors from './errors';
import helper, {HEADER_AUTHORIZED, HEADER_RUN_STATUS} from './helper';

// リクエストメソッド
const REQUEST_METHODS = new Set(['get', 'post', 'put', 'delete']);

/**
 * Axios 要求インターセプター
 *
 * @param {Object} reduxStore ストア
 * @return {Function} 要求インターセプター関数
 */
export const requestInterceptor = (reduxStore) => async function(config) {
  console.debug('Axios: Request interceptor');

  // トークンの更新
  await reduxStore.dispatch(authOperations.doRefreshTokenOperation());

  // 認証ヘッダー設定
  const headers = config.headers;
  const auth = reduxStore.getState().auth;
  if (auth?.loginInfo) {
    headers['Authorization'] = auth.loginInfo.idToken;
    headers['Content-Type'] = 'application/json';
  }

  // 認証情報ハッシュをヘッダーに設定
  const authorizedHash = auth?.authorizedHash;
  if (authorizedHash) {
    headers[HEADER_AUTHORIZED] = authorizedHash;
  }

  // ロギング
  const method = config.method;
  const api = helper.apiPath(config.url);
  console.info(
      method?.toUpperCase(), `api=${api.path}`, {
        request: config.data,
        headers: config.headers,
      });

  // 通信中表示 (明示的に無効 false を指定した場合はスキップ)
  const useLoadingImage = config.custom?.useLoadingImage;
  if (useLoadingImage !== false && REQUEST_METHODS.has(method)) {
    reduxStore.dispatch(commonOperations.startRequest());
  }
  return config;
};

/**
 * Axios 要求エラーハンドラー
 *
 * @param {Object} reduxStore ストア
 * @return {Function} 要求エラーハンドラー関数
 */
export const requestErrorHandler = (reduxStore) => async function(error) {
  console.debug('Axios: Request error handler');
  reduxStore.dispatch(commonOperations.endRequest());
  errorDialog(error, reduxStore);
  return Promise.reject(error);
};

/**
 * Axios 応答インターセプター
 *
 * @param {Object} reduxStore ストア
 * @return {Function} 応答インターセプター関数
 */
export const responseInterceptor = (reduxStore) => async function(response) {
  console.debug('Axios: Response interceptor');

  // ロギング
  try {
    const method = response.config?.method;
    const api = helper.apiPath(response.request?.responseURL);
    console.info(method?.toUpperCase(), `api=${api.path}`,
        helper.statusText(response), {response: {
          body: response.data,
          headers: response.headers,
        }});

    // 通信中表示終了 (明示的に無効 false を指定した場合はスキップ)
    const useLoadingImage = response.config?.custom?.useLoadingImage;
    if (useLoadingImage !== false && REQUEST_METHODS.has(method)) {
      reduxStore.dispatch(commonOperations.endRequest());
    }

    // 状態更新
    const skipUpdateStatus = response.config?.custom?.skipUpdateStatus;
    if (!skipUpdateStatus) {
      const headers = response.headers;

      // 認証ハッシュ更新
      const authHeader = helper.parseHeader(headers, HEADER_AUTHORIZED);
      store.updateAuthorizedHash(authHeader);

      // 業務運用状態解析 (業務時間外/業務閉塞中判定)
      const statHeader = helper.parseHeader(headers, HEADER_RUN_STATUS);
      store.parseRunningStatus(statHeader);
    }

    // 正常終了
    return Promise.resolve(response);
  } catch (e) {
    console.error('Unexpected exception', e);
    return Promise.reject(e);
  }
};

/**
 * Axios 応答エラーハンドラー
 *
 * @param {Object} reduxStore ストア
 * @return {Function} 応答エラーハンドラー関数
 */
export const responseErrorHandler = (reduxStore) => async function(error) {
  console.debug('Axios: Response error handler');
  reduxStore.dispatch(commonOperations.endRequest());
  errorDialog(error, reduxStore);
  return Promise.reject(error);
};

// ------------------------------------------------------------------------------
// エラーメッセージ表示
// ------------------------------------------------------------------------------
/**
 * エラーダイアログ表示
 *
 * @param {Object} error エラーオブジェクト
 * @param {Object} reduxStore ストア
 * @return {Promise} Promiseオブジェクト
 */
async function errorDialog(error, reduxStore) {
  const api = helper.apiPath(error.config?.url);
  const method = error.config?.method?.toUpperCase();

  // レスポンス時のエラー
  const response = error.response;
  if (response) {
    // 認証情報ハッシュ更新
    const headers = response.headers;
    const authHeader = helper.parseHeader(headers, HEADER_AUTHORIZED);
    await store.updateAuthorizedHash(authHeader);

    // バックエンド応答判定
    const backendError = await errors.backendError(headers, response.status);

    // バックエンド応答毎のメッセージ表示
    if (backendError) {
      console.error(
          method, `api=${api.path}`,
          helper.statusText(response),
          `reason=${backendError.reason}`, {response});

      // スナックメッセージ更新
      if (backendError.snackUpdate) {
        backendError.snackUpdate(reduxStore.dispatch);
      }

      // エラーダイアログ表示
      const dialog = {
        message: backendError.message,
        action: await dialogAction(backendError.action, headers),
        highPriority: backendError.lowPriority ? false : true,
      };
      showMessage(reduxStore.dispatch, response, error.config, dialog);
      return;
    }

    // HTTPステータス毎のメッセージ表示
    const serviceError = errors.serviceError(response.status);
    if (serviceError) {
      console.error(
          method, `api=${api.path}`,
          helper.statusText(response), {response});

      // エラーダイアログ表示
      const dialog = {
        message: serviceError.message,
        action: await dialogAction(serviceError.action, headers),
        highPriority: serviceError.lowPriority ? false : true,
      };
      showMessage(reduxStore.dispatch, response, error.config, dialog);
      return;
    }
  }

  // エラーログ出力
  console.error(method, `api=${api.path}`, error);

  // システムエラーメッセージ表示
  error.response = error.response || {};
  showMessage(reduxStore.dispatch, response, error.config, {
    message: 'TKSCS00001S',
    highPriority: true,
  });
}

/**
 * エラーメッセージ表示
 * @param {function} dispatch ディスパッチ関数
 * @param {object} response Axios応答オブジェクト
 * @param {object} config Axios設定オブジェクト
 * @param {object} dialog メッセージダイアログ設定
 */
function showMessage(dispatch, response, config, dialog) {
  // カスタムエラー処理
  const showDialog = customError(response, config, dialog);

  // エラーダイアログ表示
  if (showDialog) {
    dispatch(commonOperations.doShowMessage(dialog));
  }
}

/**
 * カスタムエラー処理
 * @param {object} response Axios応答オブジェクト
 * @param {object} config Axios設定オブジェクト
 * @param {object} dialog メッセージダイアログ設定
 * @return {boolean} エラーダイアログを表示するかどうか
 */
function customError(response, config, dialog) {
  const onError = config?.custom?.onError;

  // カスタム設定なし - エラーダイアログを表示
  if (!onError) {
    return true;
  }

  // エラーメッセージ
  const message = {
    id: dialog.message,
    text: intl?.formatMessage({id: 'message.' + dialog.message}),
  };

  // コールバック関数実行
  let resBody = onError;
  if (typeof onError === 'function') {
    try {
      resBody = onError(message);
    } catch (err) {
      console.error('Failure in backend API on error', err);
      return true;
    }
  }

  // 応答ボディにカスタムデータを設定
  if (typeof resBody === 'object') {
    response.data = {...response.data, ...resBody};
    return false;
  }

  // 応答ボディにエラーメッセージを設定
  if (typeof resBody === 'string') {
    response.data = response.data || {};
    response.data[resBody] = message;
    return false;
  }
  return false;
}

// エラーダイアログのボタン押下処理
async function dialogAction(errAction, headers) {
  if (!errAction) {
    return null;
  }

  // ボタン押下時に業務運用状態を更新
  return async function() {
    const statHeader = helper.parseHeader(headers, HEADER_RUN_STATUS);
    await store.updateRunningStatus(statHeader);

    // エラー理由毎の画面遷移処理
    await errAction();
  };
}

// ------------------------------------------------------------------------------
// エクスポート
// ------------------------------------------------------------------------------
export default {
  requestInterceptor,      // 要求インターセプター
  requestErrorHandler,     // 要求エラーハンドラー
  responseInterceptor,     // 応答インターセプター
  responseErrorHandler,    // 応答エラーハンドラー
};
